<template>
  <div>
    <!-- Contenu principal (affiché si shouldShowButton est vrai) -->
    <div v-if="shouldShowButton">
      <!-- Toast notification -->
      <div v-if="showNotification" class="forum-toast" :class="{ 'toast-visible': showNotification }">
        <div class="toast-content">
          <i class="fas fa-comments"></i>
          <span>{{ notificationMessage }}</span>
        </div>
      </div>

      <!-- STATE 1: CLOSED / FAB -->
      <div class="chat-closed-state" v-if="!isOpen">
        <transition name="bubble-slide">
          <div v-if="showInvitationBubble" class="invitation-bubble" @click="openChat">
            <button class="bubble-close" @click.stop="dismissBubble"><i class="fas fa-times"></i></button>
            <div class="bubble-header">
              <div class="bubble-avatar topup"><i class="fas fa-comments"></i></div>
              <span class="bubble-title">Top‑up Forum</span>
            </div>
            <div class="bubble-text">
              💬 <strong>Join the conversation!</strong> Chat with managers and other top‑ups in the global forum.
            </div>
            <div class="bubble-footer">
              <span>Tap to open</span>
              <i class="fas fa-arrow-right"></i>
            </div>
          </div>
        </transition>

        <button class="main-fab topup-fab" @click="openChat">
          <div class="fab-pulse"></div>
          <i class="fas fa-comment-dots"></i>
          <span v-if="totalUnread > 0" class="fab-badge">{{ totalUnread > 99 ? '99+' : totalUnread }}</span>
        </button>
      </div>

      <!-- STATE 2: MAIN CHAT WINDOW -->
      <transition name="window-slide">
        <div v-if="isOpen" class="chat-window-overlay">
          <div class="sidebar-chat-window topup-theme" :class="{ fullscreen: isFullscreen }">
            <!-- LEFT SIDEBAR -->
            <div class="chat-sidebar">
              <div class="sidebar-logo topup-logo"><i class="fas fa-user-tie"></i></div>
              <div class="sidebar-divider"></div>
              <button class="sidebar-channel-btn" :class="{ active: activeTab === 'manager' }" @click="switchTab('manager')" title="Global Forum">
                <i class="fas fa-globe"></i>
                <span v-if="managerUnread > 0" class="sidebar-channel-badge">{{ managerUnread > 9 ? '9+' : managerUnread }}</span>
                <span class="sidebar-tooltip">Forum</span>
              </button>
              <button class="sidebar-channel-btn" :class="{ active: activeTab === 'clients' }" @click="switchTab('clients')" title="My Clients">
                <i class="fas fa-users"></i>
                <span v-if="clientsTotalUnread > 0" class="sidebar-channel-badge">{{ clientsTotalUnread > 9 ? '9+' : clientsTotalUnread }}</span>
                <span class="sidebar-tooltip">Clients</span>
              </button>
              <div class="sidebar-bottom">
                <div class="sidebar-divider"></div>
                <button class="sidebar-settings-btn" @click="closeChat" title="Close"><i class="fas fa-times"></i><span class="sidebar-tooltip">Close</span></button>
                <button class="sidebar-settings-btn" @click="toggleFullscreen" title="Fullscreen"><i class="fas" :class="isFullscreen ? 'fa-compress' : 'fa-expand'"></i><span class="sidebar-tooltip">{{ isFullscreen ? 'Shrink' : 'Expand' }}</span></button>
              </div>
            </div>

            <!-- MAIN CONTENT -->
            <div class="chat-main">
              <!-- ========== FORUM GLOBAL ========== -->
              <div v-if="activeTab === 'manager'" class="chat-view">
                <div class="chat-header forum-header">
                  <div class="chat-header-avatar forum"><i class="fas fa-globe"></i></div>
                  <div class="chat-header-info">
                    <h4>Global Top‑up Forum</h4>
                    <span><i class="fas fa-users"></i> {{ totalForumMembers }} members · all managers & top‑ups</span>
                  </div>
                </div>
                <div class="chat-messages" ref="managerMessagesContainer" @scroll="handleScroll('manager')">
                  <div v-if="managerLoading" class="loading-state"><i class="fas fa-spinner spin"></i> Loading...</div>
                  <div v-else-if="managerMessages.length === 0" class="empty-state">
                    <div class="empty-avatar forum"><i class="fas fa-comments fa-2x"></i></div>
                    <h3>No messages yet</h3>
                    <p>Be the first to start the conversation!</p>
                  </div>
                  <template v-else>
                    <div v-for="(msg, idx) in managerMessages" :key="msg.id || idx" class="message-group">
                      <div v-if="showDateSeparator(msg, idx, managerMessages)" class="date-separator"><span>{{ formatDateSeparator(msg.timestamp) }}</span></div>
                      <div :class="['message-row', msg.is_mine ? 'sent' : 'received']">
                        <div class="msg-avatar-small" :class="{ manager: !msg.is_mine && msg.is_manager, topup: !msg.is_mine && !msg.is_manager }">
                          <i :class="msg.is_mine ? 'fas fa-user' : (msg.is_manager ? 'fas fa-chalkboard-user' : 'fas fa-user-tie')"></i>
                        </div>
                        <div class="msg-bubble forum-bubble" :class="{ sent: msg.is_mine }">
                          <div class="msg-sender" v-if="!msg.is_mine">
                            <span class="sender-name">{{ msg.sender_name }}</span>
                            <span class="sender-role" :class="msg.is_manager ? 'manager-role' : 'topup-role'">
                              {{ msg.is_manager ? 'Manager' : 'Top‑up' }}
                            </span>
                          </div>
                          <div v-if="!msg.message_type || msg.message_type === 'text'" class="msg-bubble-text" v-html="formatMessage(msg.content)"></div>
                          <div v-else-if="msg.message_type === 'image'" class="media-message">
                            <img :src="msg.file_url" class="msg-image" @click="zoomImage(msg.file_url)" @error="handleImageError" />
                            <div v-if="msg.content" class="image-caption">{{ msg.content }}</div>
                          </div>
                          <div v-else-if="msg.message_type === 'file'" class="file-message" @click="openFile(msg.file_url)">
                            <i :class="getFileIcon(msg.file_name)"></i>
                            <div class="file-info"><span>{{ msg.file_name }}</span><span>{{ formatFileSize(msg.file_size) }}</span></div>
                            <i class="fas fa-download"></i>
                          </div>
                          <div class="msg-meta"><span>{{ formatTime(msg.timestamp) }}</span><i v-if="msg.is_mine" class="fas" :class="msg.read ? 'fa-check-double read' : 'fa-check'"></i></div>
                        </div>
                      </div>
                    </div>
                  </template>
                  <button v-if="!managerIsAtBottom && managerMessages.length" class="scroll-down" @click="scrollToBottom('manager')">
                    <i class="fas fa-arrow-down"></i><span v-if="managerNewCount">{{ managerNewCount }}</span>
                  </button>
                </div>
                <div class="chat-input-area">
                  <div v-if="managerSelectedFile" class="file-preview-bar">
                    <div class="file-preview-content">
                      <img v-if="managerPreviewType === 'image'" :src="managerPreviewUrl" class="file-preview-thumb" />
                      <div v-else class="file-preview-icon"><i :class="getFileIcon(managerSelectedFile.name)"></i></div>
                      <div class="file-preview-info"><span>{{ managerSelectedFile.name }}</span><span>{{ formatFileSize(managerSelectedFile.size) }}</span></div>
                    </div>
                    <button class="file-preview-remove" @click="managerClearFile"><i class="fas fa-times"></i></button>
                  </div>
                  <div class="input-area-wrapper">
                    <button class="attach-btn" @click="$refs.managerFileInput.click()">
                      <i class="fas fa-paperclip"></i>
                    </button>
                    <div class="input-group">
                      <input
                        type="text"
                        v-model="managerDraft"
                        placeholder="Write your message to the forum..."
                        class="chat-input"
                      />
                      <button
                        class="send-btn"
                        :class="{'forum-send': true, 'active': managerDraft.trim() || managerSelectedFile}"
                        @click="sendManagerMessage"
                        :disabled="managerSending || (!managerDraft.trim() && !managerSelectedFile)"
                      >
                        <i v-if="!managerSending" class="fas fa-paper-plane"></i>
                        <i v-else class="fas fa-spinner spin"></i>
                      </button>
                    </div>
                  </div>
                  <input type="file" ref="managerFileInput" style="display:none" @change="handleFileSelect($event, 'manager')" />
                </div>
              </div>

              <!-- ========== CLIENTS TAB ========== -->
              <div v-if="activeTab === 'clients'" class="chat-view">
                <div v-if="!activeClient" class="contacts-panel">
                  <div class="contacts-header">
                    <i class="fas fa-users"></i> My Clients
                    <input type="text" v-model="clientSearch" placeholder="Search..." class="contacts-search" />
                    <button class="btn-whatsapp" @click="openWhatsAppDialog">
                      <i class="fab fa-whatsapp"></i>
                    </button>
                  </div>
                  <div class="contacts-list">
                    <div v-for="client in filteredClients" :key="client.downline_id" class="contact-item" :class="{ active: activeClient && activeClient.downline_id === client.downline_id }" @click="openClientChat(client)">
                      <img :src="client.avatar || defaultAvatar" class="contact-avatar" />
                      <div class="contact-info">
                        <div class="contact-name">{{ client.full_name }}</div>
                        <div class="contact-preview">{{ truncateMessage(client.last_message, 50) || 'No messages yet' }}</div>
                      </div>
                      <span v-if="client.unread_count > 0" class="contact-unread">{{ client.unread_count > 9 ? '9+' : client.unread_count }}</span>
                    </div>
                    <div v-if="filteredClients.length === 0" class="empty-contacts">No clients yet</div>
                  </div>
                </div>
                <div v-else class="chat-view">
                  <div class="chat-header client-header">
                    <button class="back-btn" @click="activeClient = null"><i class="fas fa-arrow-left"></i></button>
                    <div class="chat-header-avatar client"><i class="fas fa-user"></i></div>
                    <div class="chat-header-info"><h4>{{ activeClient.full_name }}</h4><span>Your sponsored client</span></div>
                  </div>
                  <div class="chat-messages" ref="clientMessagesContainer" @scroll="handleScroll('client')">
                    <div v-if="clientLoading" class="loading-state"><i class="fas fa-spinner spin"></i> Loading...</div>
                    <div v-else-if="clientMessages.length === 0" class="empty-state">
                      <div class="empty-avatar client"><i class="fas fa-user fa-2x"></i></div>
                      <h3>No messages yet</h3><p>Start a conversation with {{ activeClient.full_name }}</p>
                    </div>
                    <template v-else>
                      <div v-for="(msg, idx) in clientMessages" :key="msg.id || idx" class="message-group">
                        <div v-if="showDateSeparator(msg, idx, clientMessages)" class="date-separator"><span>{{ formatDateSeparator(msg.timestamp) }}</span></div>
                        <div :class="['message-row', msg.is_mine ? 'sent' : 'received']">
                          <div class="msg-avatar-small" :class="{ client: !msg.is_mine }">
                            <i :class="msg.is_mine ? 'fas fa-user' : 'fas fa-user'"></i>
                          </div>
                          <div class="msg-bubble client-bubble" :class="{ sent: msg.is_mine }">
                            <div v-if="!msg.message_type || msg.message_type === 'text'" class="msg-bubble-text" v-html="formatMessage(msg.content)"></div>
                            <div v-else-if="msg.message_type === 'image'" class="media-message">
                              <img :src="msg.file_url" class="msg-image" @click="zoomImage(msg.file_url)" />
                            </div>
                            <div v-else-if="msg.message_type === 'file'" class="file-message" @click="openFile(msg.file_url)">
                              <i :class="getFileIcon(msg.file_name)"></i>
                              <div class="file-info"><span>{{ msg.file_name }}</span><span>{{ formatFileSize(msg.file_size) }}</span></div>
                              <i class="fas fa-download"></i>
                            </div>
                            <div class="msg-meta"><span>{{ formatTime(msg.timestamp) }}</span><i v-if="msg.is_mine" class="fas" :class="msg.read ? 'fa-check-double read' : 'fa-check'"></i></div>
                          </div>
                        </div>
                      </div>
                    </template>
                    <button v-if="!clientIsAtBottom && clientMessages.length" class="scroll-down" @click="scrollToBottom('client')">
                      <i class="fas fa-arrow-down"></i><span v-if="clientNewCount">{{ clientNewCount }}</span>
                    </button>
                  </div>
                  <div class="chat-input-area">
                    <div v-if="clientSelectedFile" class="file-preview-bar">
                      <div class="file-preview-content">
                        <img v-if="clientPreviewType === 'image'" :src="clientPreviewUrl" class="file-preview-thumb" />
                        <div v-else class="file-preview-icon"><i :class="getFileIcon(clientSelectedFile.name)"></i></div>
                        <div class="file-preview-info"><span>{{ clientSelectedFile.name }}</span><span>{{ formatFileSize(clientSelectedFile.size) }}</span></div>
                      </div>
                      <button class="file-preview-remove" @click="clientClearFile"><i class="fas fa-times"></i></button>
                    </div>
                    <div class="input-area-wrapper">
                      <button class="attach-btn" @click="$refs.clientFileInput.click()">
                        <i class="fas fa-paperclip"></i>
                      </button>
                      <div class="input-group">
                        <input
                          type="text"
                          v-model="clientDraft"
                          :placeholder="'Message ' + (activeClient.full_name) + '...'"
                          class="chat-input"
                        />
                        <button
                          class="send-btn"
                          :class="{'client-send': true, 'active': clientDraft.trim() || clientSelectedFile}"
                          @click="sendClientMessage"
                          :disabled="clientSending || (!clientDraft.trim() && !clientSelectedFile)"
                        >
                          <i v-if="!clientSending" class="fas fa-paper-plane"></i>
                          <i v-else class="fas fa-spinner spin"></i>
                        </button>
                      </div>
                    </div>
                    <input type="file" ref="clientFileInput" style="display:none" @change="handleFileSelect($event, 'client')" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- Image Modal -->
      <div v-if="zoomedImage" class="image-modal" @click="zoomedImage = null">
        <img :src="zoomedImage" @click.stop /><button class="close-modal" @click.stop="zoomedImage = null"><i class="fas fa-times"></i></button>
      </div>
    </div>

    <!-- WhatsApp DIALOG with teleport to body to ensure it appears above everything -->
    <teleport to="body">
      <transition name="fade">
        <div v-if="whatsappDialogVisible" class="modal-backdrop" @click.self="closeWhatsAppDialog">
          <div class="modal modal-whatsapp">
            <div class="modal__head">
              <div class="modal__head-label">
                <span class="modal__head-icon"><i class="fab fa-whatsapp"></i></span>
                <div><div class="modal__head-title">Share via WhatsApp</div><div class="modal__head-sub">Select a client to send a message</div></div>
              </div>
              <button class="icon-btn" @click="closeWhatsAppDialog"><i class="fas fa-times"></i></button>
            </div>
            <div class="modal__body">
              <div class="search-wrap" style="margin-bottom: 12px;">
                <i class="fas fa-search search-wrap__icon"></i>
                <input v-model="whatsappSearch" type="text" placeholder="Filter clients..." class="search-wrap__input" />
              </div>
              <div class="whatsapp-client-list">
                <div v-for="client in filteredWhatsappDownlines" :key="client.downline_id" class="whatsapp-client-item">
                  <label class="whatsapp-client-label">
                    <input type="radio" v-model="selectedClientId" :value="client.downline_id" class="client-radio" />
                    <span class="client-name">{{ client.full_name }}</span>
                    <span class="client-phone" v-if="client.phone_number">{{ client.phone_number }}</span>
                    <span v-else class="no-phone">No phone</span>
                  </label>
                </div>
                <div v-if="filteredWhatsappDownlines.length === 0" class="empty-small">No clients found</div>
              </div>
              <div class="selected-info">
                <span v-if="selectedClientId">1 client selected</span>
                <span v-else>No client selected</span>
              </div>
              <div class="form-group">
                <label>Message template</label>
                <textarea v-model="whatsappMessageTemplate" rows="2" class="compose-box__textarea"></textarea>
                <div class="template-hint">Use {name} for client's name, https://test-myapp.pettycashglobal.com/ for chat link</div>
              </div>
              <div class="link-preview">
                <div class="preview-label">Preview (selected client):</div>
                <code>{{ whatsappPreview }}</code>
              </div>
            </div>
            <div class="modal__foot modal__foot--column">
              <button class="btn btn--ghost" @click="closeWhatsAppDialog">Cancel</button>
              <button class="btn btn--whatsapp" @click="sendWhatsAppToSelected" :disabled="!selectedClientId"><i class="fab fa-whatsapp"></i> Send to client</button>
              <button class="btn btn--secondary" @click="copySingleLink" :disabled="!selectedClientId"><i class="fas fa-copy"></i> Copy chat link</button>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'TopupMessaging',
  data() {
    return {
      // UI
      isOpen: false,
      isFullscreen: false,
      activeTab: 'manager',
      showInvitationBubble: false,
      defaultAvatar: require('@/assets/images/avatars/1.png'),

      // Forum global
      managerMessages: [],
      chatBaseUrl: 'https://test-myapp.pettycashglobal.com',
      managerDraft: '',
      managerSelectedFile: null,
      managerPreviewUrl: null,
      managerPreviewType: null,
      managerSending: false,
      managerLoading: false,
      managerIsAtBottom: true,
      managerNewCount: 0,
      managerPollInterval: null,
      lastManagerFetch: 0,
      totalForumMembers: 0,

      // Clients
      downlinesList: [],
      activeClient: null,
      clientMessages: [],
      clientDraft: '',
      clientSelectedFile: null,
      clientPreviewUrl: null,
      clientPreviewType: null,
      clientSending: false,
      clientLoading: false,
      clientIsAtBottom: true,
      clientNewCount: 0,
      clientSearch: '',
      clientPollInterval: null,
      lastClientFetchMap: {},

      // Shared
      zoomedImage: null,
      imageErrors: {},
      historyFetchInterval: 5000,

      // Notification
      showNotification: false,
      notificationMessage: '',
      notificationTimeout: null,

      // Invitation bubble timeout
      invitationTimeout: null,

      // Badge unread (total)
      badgeUnread: 0,

      // WhatsApp dialog
      whatsappDialogVisible: false,
      whatsappSearch: '',
      selectedClientId: null,
      whatsappMessageTemplate: "Hello {name},\n\nI would like to discuss something with you.\n\nJoin the conversation: https://test-myapp.pettycashglobal.com/",
      defaultCountryCode: '237', // Cameroon
    }
  },

  computed: {
    shouldShowButton() {
      const hideRoutes = ['dashboard.chat', 'chat_history']
      const currentRoute = this.$route && this.$route.name
      return !hideRoutes.includes(currentRoute)
    },
    totalUnread() {
      return this.badgeUnread
    },
    clientsTotalUnread() {
      return this.downlinesList.reduce((sum, c) => sum + (c.unread_count || 0), 0)
    },
    filteredClients() {
      if (!this.clientSearch) return this.downlinesList
      const q = this.clientSearch.toLowerCase()
      return this.downlinesList.filter(c => c.full_name.toLowerCase().includes(q) || (c.username && c.username.toLowerCase().includes(q)))
    },
    // For WhatsApp dialog
    filteredWhatsappDownlines() {
      if (!this.whatsappSearch) return this.downlinesList
      const q = this.whatsappSearch.toLowerCase()
      return this.downlinesList.filter(c => c.full_name.toLowerCase().includes(q))
    },
  whatsappPreview() {
        if (!this.selectedClientId) return "Select a client to see preview";
        const client = this.downlinesList.find(c => c.downline_id === this.selectedClientId);
        if (!client) return "";
        const link = `${this.chatBaseUrl}/chat/chat/${client.downline_id}`;  // ← ici
        let msg = this.whatsappMessageTemplate.replace(/{name}/g, client.full_name).replace(/{link}/g, link);
        return msg.length > 100 ? msg.substring(0, 100) + "…" : msg;
      }
  },

  watch: {
    managerMessages: {
      handler() {
        this.$nextTick(() => this.scrollToBottom('manager'))
      },
      deep: true
    },
    clientMessages: {
      handler() {
        this.$nextTick(() => this.scrollToBottom('client'))
      },
      deep: true
    },
    whatsappDialogVisible(newVal) {
      console.log('🟢 whatsappDialogVisible changed to:', newVal);
    }
  },

  mounted() {
    this.checkTopupRole()
    this.startInvitationSequence()
    this.fetchTotalUnread()
    this.loadDownlinesList()
    this.startPolling()
    window.visualViewport?.addEventListener('resize', this.handleKeyboard)
  },

  beforeDestroy() {
    if (this.managerPollInterval) clearInterval(this.managerPollInterval)
    if (this.clientPollInterval) clearInterval(this.clientPollInterval)
    if (this.notificationTimeout) clearTimeout(this.notificationTimeout)
    if (this.invitationTimeout) clearTimeout(this.invitationTimeout)
  },

  methods: {
    // ---------- Initialization ----------
    async checkTopupRole() {
      try {
        const res = await axios.get('/api/v1/home/user_profile/')
        if (!res.data.top_up) {
          console.warn('User is not a top-up, component should not be used')
          this.closeChat()
        }
      } catch (e) { console.error(e) }
    },

    handleKeyboard() {
      if (window.visualViewport) {
        const heightDiff = window.innerHeight - window.visualViewport.height
        const isKeyboardOpen = heightDiff > 100
        if (isKeyboardOpen && this.isOpen) {
          this.$nextTick(() => {
            this.scrollToBottom(this.activeTab === 'manager' ? 'manager' : 'client')
          })
        }
      }
    },
    truncateMessage(text, length = 50) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '…' : text;
    },
    startInvitationSequence() {
      if (localStorage.getItem('topup_chat_bubble_dismissed')) {
        this.showInvitationBubble = false
        return
      }
      setTimeout(() => {
        this.showInvitationBubble = true
        this.invitationTimeout = setTimeout(() => {
          this.showInvitationBubble = false
          localStorage.setItem('topup_chat_bubble_dismissed', 'true')
        }, 8000)
      }, 3000)
    },
    openChat() {
      if (this.invitationTimeout) clearTimeout(this.invitationTimeout)
      this.showInvitationBubble = false
      localStorage.setItem('topup_chat_bubble_dismissed', 'true')
      this.isOpen = true
      this.activeTab = 'manager'
      this.loadManagerConversation(true, false)
      this.loadDownlinesList()
    },
    closeChat() {
      this.isOpen = false
      this.activeTab = 'manager'
      this.activeClient = null
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
    },
    dismissBubble() {
      if (this.invitationTimeout) clearTimeout(this.invitationTimeout)
      this.showInvitationBubble = false
      localStorage.setItem('topup_chat_bubble_dismissed', 'true')
    },
    switchTab(tab) {
      this.activeTab = tab
      if (tab === 'manager') {
        this.loadManagerConversation(true, false)
      } else if (tab === 'clients') {
        this.loadDownlinesList()
      }
    },

    // ---------- FORUM GLOBAL ----------
    async loadManagerConversation(markRead = true, isBackground = false) {
      if (this.managerLoading) return
      const now = Date.now()
      if (now - this.lastManagerFetch < this.historyFetchInterval && !markRead) return
      this.lastManagerFetch = now
      if (!isBackground) this.managerLoading = true
      try {
        const markParam = markRead ? 'true' : 'false'
        const res = await axios.get(`/api/v1/manager/forum/messages/?mark_read=${markParam}`)
        if (res.data.messages) {
          const oldCount = this.managerMessages.length
          this.managerMessages = res.data.messages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
          this.totalForumMembers = res.data.total_members || 0

          if (!markRead && this.managerMessages.length > oldCount) {
            const newMsg = this.managerMessages[this.managerMessages.length - 1]
            if (newMsg && !newMsg.is_mine) {
              this.showNotificationMessage(`💬 New message from ${newMsg.sender_name} in forum`)
            }
          }
          if (markRead) this.fetchTotalUnread()
          if (!isBackground) this.$nextTick(() => this.scrollToBottom('manager'))
        }
      } catch (e) {
        console.error('Forum loading error', e)
        this.managerMessages = []
      } finally {
        if (!isBackground) this.managerLoading = false
      }
    },
    async sendManagerMessage() {
      if ((!this.managerDraft.trim() && !this.managerSelectedFile) || this.managerSending) return
      this.managerSending = true
      const fd = new FormData()
      fd.append('message', this.managerDraft.trim())
      fd.append('message_type', this.managerSelectedFile ? this.getFileType(this.managerSelectedFile) : 'text')
      if (this.managerSelectedFile) fd.append('file', this.managerSelectedFile)
      try {
        await axios.post('/api/v1/manager/forum/send/', fd)
        this.managerDraft = ''
        this.managerClearFile()
        await this.loadManagerConversation(true, false)
        this.fetchTotalUnread()
      } catch (e) {
        console.error('Forum send error', e)
        this.$store.commit('setSnackBarMessage', 'Failed to send message')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      } finally {
        this.managerSending = false
      }
    },
    managerClearFile() {
      this.managerSelectedFile = null
      this.managerPreviewUrl = null
      this.managerPreviewType = null
    },

    // ---------- Clients ----------
    async loadDownlinesList() {
      try {
        const res = await axios.get('/api/v1/topup/my_downlines/')
        const downlines = (res.data.downlines || []).map(d => ({ ...d, phone_number: d.phone_number || '' }))
        this.downlinesList = downlines
      } catch (e) { console.error(e) }
    },
    async openClientChat(client) {
      this.activeClient = client
      this.clientMessages = []
      await this.loadClientMessages(client.downline_id, true, false)
    },
    async loadClientMessages(downlineId, markRead = true, isBackground = false) {
      const key = 'client_' + downlineId
      const lastFetch = this.lastClientFetchMap[key] || 0
      if (Date.now() - lastFetch < this.historyFetchInterval && !markRead) {
        return
      }
      this.lastClientFetchMap[key] = Date.now()
      if (!isBackground) this.clientLoading = true
      try {
        const markParam = markRead ? 'true' : 'false'
        const res = await axios.get(`/api/v1/topup/conversation_with_downline/${downlineId}/?mark_read=${markParam}`)
        if (res.data.messages) {
          this.clientMessages = res.data.messages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
          if (markRead) {
            const idx = this.downlinesList.findIndex(c => c.downline_id === downlineId)
            if (idx !== -1) this.downlinesList[idx].unread_count = 0
            this.fetchTotalUnread()
          }
          if (!isBackground) this.$nextTick(() => this.scrollToBottom('client'))
        }
      } catch (e) {
        console.error(e)
      } finally {
        if (!isBackground) this.clientLoading = false
      }
    },
    async sendClientMessage() {
      if ((!this.clientDraft.trim() && !this.clientSelectedFile) || this.clientSending || !this.activeClient) return
      this.clientSending = true
      const fd = new FormData()
      fd.append('downline_id', this.activeClient.downline_id)
      fd.append('message', this.clientDraft.trim())
      fd.append('message_type', this.clientSelectedFile ? this.getFileType(this.clientSelectedFile) : 'text')
      if (this.clientSelectedFile) fd.append('file', this.clientSelectedFile)
      try {
        await axios.post('/api/v1/topup/send_to_downline/', fd)
        this.clientDraft = ''
        this.clientClearFile()
        await this.loadClientMessages(this.activeClient.downline_id, true, false)
        this.fetchTotalUnread()
      } catch (e) {
        console.error(e)
      } finally {
        this.clientSending = false
      }
    },
    clientClearFile() {
      this.clientSelectedFile = null
      this.clientPreviewUrl = null
      this.clientPreviewType = null
    },

    // ---------- Shared file handling ----------
    handleFileSelect(event, origin) {
      const file = event.target.files[0]
      if (!file) return
      const maxSize = file.type.startsWith('video/') ? 2000 * 1024 * 1024 : 10 * 1024 * 1024
      if (file.size > maxSize) { alert('File too large'); return }
      if (origin === 'manager') {
        this.managerSelectedFile = file
        const reader = new FileReader()
        reader.onload = (e) => {
          this.managerPreviewUrl = e.target.result
          this.managerPreviewType = file.type.startsWith('image/') ? 'image' : 'file'
        }
        reader.readAsDataURL(file)
      } else {
        this.clientSelectedFile = file
        const reader = new FileReader()
        reader.onload = (e) => {
          this.clientPreviewUrl = e.target.result
          this.clientPreviewType = file.type.startsWith('image/') ? 'image' : 'file'
        }
        reader.readAsDataURL(file)
      }
      event.target.value = ''
    },
    getFileType(file) {
      if (file.type.startsWith('image/')) return 'image'
      if (file.type.startsWith('video/')) return 'video'
      return 'file'
    },
    getFileIcon(name) {
      if (!name) return 'fas fa-file'
      const ext = name.split('.').pop().toLowerCase()
      const icons = {
        pdf: 'fas fa-file-pdf',
        doc: 'fas fa-file-word',
        docx: 'fas fa-file-word',
        jpg: 'fas fa-file-image',
        jpeg: 'fas fa-file-image',
        png: 'fas fa-file-image',
        zip: 'fas fa-file-archive'
      }
      return icons[ext] || 'fas fa-file'
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + sizes[i]
    },
    formatTime(ts) {
      if (!ts) return ''
      return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    formatMessage(text) {
      if (!text) return ''
      return text.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank">$1</a>').replace(/\n/g, '<br>')
    },
    showDateSeparator(msg, idx, list) {
      if (idx === 0) return true
      const prev = list[idx - 1]
      if (!prev || !prev.timestamp) return false
      return new Date(prev.timestamp).toDateString() !== new Date(msg.timestamp).toDateString()
    },
    formatDateSeparator(ts) {
      if (!ts) return ''
      const d = new Date(ts)
      const today = new Date().toDateString()
      const yesterday = new Date(Date.now() - 86400000).toDateString()
      if (d.toDateString() === today) return 'Today'
      if (d.toDateString() === yesterday) return 'Yesterday'
      return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
    },
    scrollToBottom(type) {
      const ref = type === 'manager' ? 'managerMessagesContainer' : 'clientMessagesContainer'
      const el = this.$refs[ref]
      if (el) {
        el.scrollTop = el.scrollHeight
        this[type + 'IsAtBottom'] = true
        this[type + 'NewCount'] = 0
      }
    },
    handleScroll(type) {
      const ref = type === 'manager' ? 'managerMessagesContainer' : 'clientMessagesContainer'
      const el = this.$refs[ref]
      if (!el) return
      const isBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 100
      this[type + 'IsAtBottom'] = isBottom
      if (isBottom) this[type + 'NewCount'] = 0
    },
    zoomImage(url) { this.zoomedImage = url },
    handleImageError() {},
    openFile(url) { window.open(url, '_blank') },

    // Notification toast
    showNotificationMessage(msg) {
      if (this.notificationTimeout) clearTimeout(this.notificationTimeout)
      this.notificationMessage = msg
      this.showNotification = true
      this.notificationTimeout = setTimeout(() => {
        this.showNotification = false
      }, 4000)
    },

    async fetchTotalUnread() {
      try {
        const res = await axios.get('/api/v1/topup/total_unread/')
        this.badgeUnread = res.data.unread
      } catch (e) {
        console.error('Error fetching total unread:', e)
      }
    },

    // ---------- Polling ----------
    startPolling() {
      this.managerPollInterval = setInterval(() => {
        this.fetchTotalUnread()
        if (this.isOpen && this.activeTab === 'manager') {
          this.loadManagerConversation(false, true)
        }
      }, 8000)

      this.clientPollInterval = setInterval(() => {
        this.loadDownlinesList()
        if (this.isOpen && this.activeTab === 'clients' && this.activeClient) {
          this.loadClientMessages(this.activeClient.downline_id, false, true)
        }
      }, 8000)
    },

    // ---------- WhatsApp methods ----------
    openWhatsAppDialog() {
      this.selectedClientId = null
      this.whatsappSearch = ''
      this.whatsappDialogVisible = true
      console.log('[WhatsApp] Dialog opened (single selection)')
    },
    closeWhatsAppDialog() {
      this.whatsappDialogVisible = false
      this.selectedClientId = null
      this.whatsappSearch = ''
    },
    formatPhoneForWhatsApp(rawPhone) {
      if (!rawPhone) return ''
      let digits = rawPhone.toString().replace(/\D/g, '')
      if (digits.startsWith('0')) {
        digits = digits.substring(1)
      }
      if (digits.length === 9) {
        digits = this.defaultCountryCode + digits
      } else if (digits.length === 12 && digits.startsWith(this.defaultCountryCode)) {
        // already good
      } else {
        console.warn(`[WhatsApp] Non‑standard phone format: ${digits}`)
      }
      return digits ? `+${digits}` : ''
    },
    async sendWhatsAppToSelected() {
  if (!this.selectedClientId) {
    this.showSnackBar('Please select a client', 'red darken-4');
    return;
  }
  const client = this.downlinesList.find(c => c.downline_id === this.selectedClientId);
  if (!client) return;

  const rawPhone = client.phone_number || '';
  const internationalPhone = this.formatPhoneForWhatsApp(rawPhone);

  const chatLink = `${this.chatBaseUrl}/chat/chat/${client.downline_id}`;  // ← ici
  let message = this.whatsappMessageTemplate
    .replace(/{name}/g, client.full_name)
    .replace(/{link}/g, chatLink);
  const encodedMessage = encodeURIComponent(message);

  let url;
  if (internationalPhone && internationalPhone.length > 5) {
    url = `https://wa.me/${encodeURIComponent(internationalPhone)}?text=${encodedMessage}`;
  } else {
    url = `https://wa.me/?text=${encodedMessage}`;
    this.showSnackBar(`No valid phone for ${client.full_name}, opening without recipient`, 'orange');
  }
  window.open(url, '_blank');
  this.showSnackBar(`WhatsApp opened for ${client.full_name}`, 'green');
  console.log(`[WhatsApp] Opened for ${client.full_name} with URL: ${url}`);
  this.closeWhatsAppDialog();
},
    async copySingleLink() {
  if (!this.selectedClientId) {
    this.showSnackBar('No client selected', 'red darken-4');
    return;
  }
  const client = this.downlinesList.find(c => c.downline_id === this.selectedClientId);
  if (!client) return;
  const link = `${this.chatBaseUrl}/chat/chat/${client.downline_id}`;  // ← ici
  try {
    await navigator.clipboard.writeText(link);
    this.showSnackBar(`Chat link for ${client.full_name} copied!`, 'green');
  } catch (err) {
    this.showSnackBar('Failed to copy link', 'red darken-4');
    console.error(err);
  }
},
    showSnackBar(message, color) {
      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', color)
      this.$store.commit('activateSnackBar', true)
    }
  }
}
</script>


<style scoped>
/* ========== STYLES AMÉLIORÉS POUR LE BOUTON FLOTTANT ========== */
.chat-closed-state {
  position: fixed;
  bottom: 80px;
  right: 30px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

.invitation-bubble {
  background: white;
  border-radius: 28px;
  padding: 18px 24px;
  max-width: 300px;
  box-shadow: 0 20px 35px -8px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.02);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: none;
  position: relative;
  overflow: hidden;
}
.invitation-bubble::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #f59e0b, #d97706);
}
.invitation-bubble:hover {
  transform: translateY(-5px) scale(1.02);
  box-shadow: 0 25px 40px -12px rgba(0,0,0,0.25);
}
.bubble-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f1f5f9;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s;
  color: #94a3b8;
  font-size: 10px;
}
.invitation-bubble:hover .bubble-close {
  opacity: 1;
}
.bubble-close:hover {
  background: #e2e8f0;
  color: #64748b;
}
.bubble-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}
.bubble-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.bubble-avatar.topup {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  box-shadow: 0 4px 10px rgba(245,158,11,0.3);
}
.bubble-avatar i {
  color: white;
  font-size: 18px;
}
.bubble-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}
.bubble-text {
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
  margin-left: 52px;
  margin-bottom: 12px;
}
.bubble-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
  color: #f59e0b;
  margin-top: 4px;
}
.main-fab {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 16px rgba(245, 158, 11, 0.4), 0 0 0 4px rgba(245,158,11,0.2);
  color: white;
  font-size: 22px;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  animation: fab-pulse 2s infinite;
}
.main-fab:hover {
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 12px 28px rgba(245, 158, 11, 0.5);
}
.fab-pulse {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: rgba(245,158,11,0.4);
  animation: ring 1.5s infinite;
}
@keyframes ring {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  100% {
    transform: scale(1.3);
    opacity: 0;
  }
}
@keyframes fab-pulse {
  0%, 100% {
    box-shadow: 0 6px 16px rgba(245,158,11,0.4), 0 0 0 4px rgba(245,158,11,0.2);
  }
  50% {
    box-shadow: 0 6px 20px rgba(245,158,11,0.6), 0 0 0 6px rgba(245,158,11,0.1);
  }
}
.fab-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: 800;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #0f172a;
  box-shadow: 0 2px 6px rgba(239, 68, 68, 0.5);
  animation: bounce-in 0.5s;
}

/* Toast notification */
.forum-toast {
  position: fixed;
  bottom: 100px;
  right: 100px;
  background: #1e293b;
  color: #f1f5f9;
  border-radius: 40px;
  padding: 10px 20px;
  box-shadow: 0 10px 25px -5px rgba(0,0,0,0.2), 0 0 0 1px rgba(245,158,11,0.3);
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 2000;
  transform: translateX(120%);
  transition: transform 0.3s ease;
  font-size: 14px;
  font-weight: 500;
  backdrop-filter: blur(8px);
}
.forum-toast.toast-visible {
  transform: translateX(0);
}
.toast-content i {
  color: #f59e0b;
  font-size: 16px;
}

/* ========== STYLES GÉNÉRAUX ========== */
.chat-window-overlay { position: fixed; bottom: 30px; right: 30px; z-index: 1002; }
.sidebar-chat-window { background: #1e293b; border-radius: 24px; overflow: hidden; box-shadow: 0 25px 80px rgba(0,0,0,0.5); display: flex; width: 440px; height: 600px; max-height: calc(100vh - 80px); transition: all 0.3s; }
.sidebar-chat-window.fullscreen { width: calc(100vw - 60px); height: calc(100vh - 60px); max-width: 1200px; }
.chat-sidebar { width: 72px; background: #0f172a; border-right: 1px solid #334155; display: flex; flex-direction: column; align-items: center; padding: 16px 0; gap: 6px; flex-shrink: 0; }
.sidebar-logo { width: 40px; height: 40px; border-radius: 14px; display: flex; align-items: center; justify-content: center; margin-bottom: 8px; }
.sidebar-channel-btn { width: 48px; height: 48px; border-radius: 16px; display: flex; align-items: center; justify-content: center; cursor: pointer; border: none; background: transparent; color: #64748b; font-size: 18px; position: relative; }
.sidebar-channel-btn.active { background: linear-gradient(135deg, #f59e0b, #d97706); color: white; box-shadow: 0 4px 15px rgba(245,158,11,0.3); }
.sidebar-channel-badge { position: absolute; top: 2px; right: 2px; background: #ef4444; color: white; font-size: 9px; min-width: 16px; height: 16px; border-radius: 8px; display: flex; align-items: center; justify-content: center; border: 2px solid #0f172a; }
.sidebar-tooltip { position: absolute; left: 60px; background: #1e293b; color: #e2e8f0; padding: 6px 12px; border-radius: 8px; font-size: 12px; white-space: nowrap; opacity: 0; pointer-events: none; transition: all 0.2s; border: 1px solid #334155; }
.sidebar-channel-btn:hover .sidebar-tooltip { opacity: 1; left: 64px; }
.sidebar-bottom { margin-top: auto; display: flex; flex-direction: column; align-items: center; gap: 6px; padding-top: 8px; }
.sidebar-settings-btn { width: 40px; height: 40px; border-radius: 12px; display: flex; align-items: center; justify-content: center; cursor: pointer; border: none; background: transparent; color: #475569; font-size: 15px; position: relative; }
.sidebar-settings-btn:hover { background: rgba(255,255,255,0.05); color: #e2e8f0; }
.chat-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.chat-view { display: flex; flex-direction: column; height: 100%; }
.chat-header { padding: 16px 20px; border-bottom: 1px solid #334155; display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
.chat-header-avatar { width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.chat-header-avatar i { color: white; font-size: 16px; }
.chat-header-info { flex: 1; }
.chat-header-info h4 { font-size: 15px; font-weight: 700; color: #f1f5f9; margin: 0; }
.chat-header-info span { font-size: 12px; color: #64748b; }
.back-btn {
  display: flex !important;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #334155;
  border: none;
  color: white;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  transition: background 0.2s;
}
.back-btn:hover { background: #475569; }
.chat-messages { flex: 1; padding: 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 16px; background: linear-gradient(180deg, #0f172a 0%, #1e293b 100%); position: relative; }
.loading-state, .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; gap: 12px; color: #64748b; }
.message-group { display: flex; flex-direction: column; gap: 4px; }
.message-row { display: flex; gap: 10px; align-items: flex-end; }
.message-row.sent { flex-direction: row-reverse; }
.msg-avatar-small { width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; background: linear-gradient(135deg, #1976d2, #1565c0); }
.msg-avatar-small.manager { background: linear-gradient(135deg, #f59e0b, #d97706); }
.msg-avatar-small.topup { background: linear-gradient(135deg, #10b981, #059669); }
.msg-avatar-small i { color: white; font-size: 11px; }
.msg-bubble {
  background: #334155;
  padding: 10px 14px;
  border-radius: 18px;
  border-bottom-left-radius: 4px;
  max-width: 75%;
  position: relative;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}
.message-row.sent .msg-bubble {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border-bottom-left-radius: 18px;
  border-bottom-right-radius: 4px;
  color: white;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}
.msg-bubble.received {
  background: #e5e7eb;
  border: 1px solid #d1d5db;
  color: #1f2937;
  border-bottom-left-radius: 4px;
}
.msg-bubble.received .msg-bubble-text {
  color: #1f2937;
}
.msg-bubble-text {
  font-size: 13px;
  line-height: 1.5;
  word-wrap: break-word;
}
.msg-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 4px;
  font-size: 10px;
  color: #64748b;
}
.message-row.sent .msg-meta {
  color: rgba(255,255,255,0.7);
}
.msg-meta i.read {
  color: #60a5fa;
}
.message-row.sent .msg-meta i.read {
  color: rgba(255,255,255,0.9);
}
.client-bubble.sent {
  background: linear-gradient(135deg, #3b82f6, #2563eb) !important;
}
.client-bubble.received {
  background: #e5e7eb !important;
  border: 1px solid #d1d5db;
}
.date-separator {
  text-align: center;
  margin: 8px 0;
  position: relative;
}
.date-separator::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, #334155, transparent);
}
.date-separator span {
  position: relative;
  background: #0f172a;
  padding: 0 12px;
  font-size: 11px;
  color: #475569;
  font-weight: 500;
}
.msg-image { max-width: 200px; border-radius: 12px; cursor: pointer; }
.file-message { display: flex; align-items: center; gap: 10px; padding: 8px 12px; background: rgba(0,0,0,0.2); border-radius: 10px; cursor: pointer; }
.file-info { flex: 1; display: flex; flex-direction: column; }
.file-info span:first-child { font-size: 12px; font-weight: 500; color: #e2e8f0; }
.file-info span:last-child { font-size: 10px; color: #64748b; }
.scroll-down { position: absolute; bottom: 20px; right: 20px; background: #f59e0b; color: white; border-radius: 25px; padding: 8px 12px; cursor: pointer; display: flex; align-items: center; gap: 6px; box-shadow: 0 4px 12px rgba(245,158,11,0.4); }

/* ========== NOUVELLE STRUCTURE DE SAISIE AVEC BOUTON INTÉGRÉ ========== */
.chat-input-area {
  padding: 12px 16px;
  border-top: 1px solid #334155;
  background: #1e293b;
  flex-shrink: 0;
}

/* Zone d'attachement et groupe de saisie */
.input-area-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Bouton d'attachement */
.attach-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #334155;
  color: #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.attach-btn:hover {
  background: #475569;
  color: #f1f5f9;
  transform: scale(1.02);
}

.attach-btn:active {
  transform: scale(0.96);
}

/* Groupe input + bouton d'envoi intégré */
.input-group {
  flex: 1;
  display: flex;
  align-items: center;
  background: #0f172a;
  border-radius: 28px;
  border: 1px solid #334155;
  padding: 4px 4px 4px 18px;
  transition: all 0.2s ease;
}

.input-group:focus-within {
  border-color: #f59e0b;
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2);
}

/* Champ de saisie */
.chat-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #f1f5f9;
  font-size: 14px;
  padding: 12px 0;
  min-width: 0;
}

.chat-input::placeholder {
  color: #64748b;
  font-size: 13px;
}

/* Bouton d'envoi - intégré dans le champ */
.send-btn {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: linear-gradient(135deg, #64748b, #475569);
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
  margin-left: 4px;
  flex-shrink: 0;
}

/* État actif (message ou fichier présent) */
.send-btn.active {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.4);
}

.send-btn.active:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.5);
}

.send-btn.active:active {
  transform: scale(0.94);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  background: linear-gradient(135deg, #64748b, #475569);
  color: #94a3b8;
}

/* Version forum (couleur orange) */
.send-btn.forum-send.active {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

/* Version client (bleue) */
.send-btn.client-send.active {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

/* File preview */
.file-preview-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: #0f172a;
  border-radius: 20px;
  margin-bottom: 12px;
  border-left: 3px solid #f59e0b;
}

.file-preview-content {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.file-preview-thumb {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: cover;
}

.file-preview-icon {
  width: 36px;
  height: 36px;
  background: #334155;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f59e0b;
}

.file-preview-info {
  display: flex;
  flex-direction: column;
}

.file-preview-info span:first-child {
  font-size: 12px;
  color: #e2e8f0;
  font-weight: 500;
}

.file-preview-info span:last-child {
  font-size: 10px;
  color: #64748b;
}

.file-preview-remove {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.file-preview-remove:hover {
  background: #334155;
  color: #f1f5f9;
}

/* Contacts panel */
.contacts-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.contacts-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #334155;
  color: #f1f5f9;
  flex-wrap: wrap;
}
.contacts-search {
  flex: 1;
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 20px;
  padding: 6px 12px;
  color: white;
  min-width: 120px;
}
.contacts-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  max-height: 100%;
}
.contact-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.2s;
  margin-bottom: 4px;
}
.contact-item:hover {
  background: #1e293b;
}
.contact-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}
.contact-info {
  flex: 1;
}
.contact-name {
  font-weight: 600;
  color: #f1f5f9;
  font-size: 14px;
}
.contact-preview {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.contact-unread {
  background: #f59e0b;
  color: #1e293b;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: bold;
}
.empty-contacts {
  text-align: center;
  color: #64748b;
  padding: 30px;
}
.image-modal {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
.image-modal img {
  max-width: 90vw;
  max-height: 90vh;
}
.close-modal {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255,255,255,0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  cursor: pointer;
}
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Styles pour le forum */
.chat-header.forum-header {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.1), transparent);
}
.chat-header-avatar.forum {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}
.msg-sender {
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.sender-name {
  font-weight: 600;
  font-size: 12px;
  color: #cbd5e1;
}
.sender-role {
  font-size: 9px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 500;
}
.manager-role {
  background: #3b82f6;
  color: white;
}
.topup-role {
  background: #10b981;
  color: white;
}
.forum-bubble.received .msg-sender {
  margin-bottom: 4px;
}
.empty-avatar.forum {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

/* ========== MEDIA QUERIES RESPONSIVE ========== */
@media (max-width: 768px) {
  .sidebar-chat-window {
    width: calc(100vw - 40px);
    height: calc(100vh - 100px);
  }
  .back-btn {
    display: flex !important;
  }
  .attach-btn {
    width: 40px;
    height: 40px;
  }
  .send-btn {
    width: 38px;
    height: 38px;
  }
  .input-group {
    padding: 3px 3px 3px 14px;
  }
  .chat-input {
    padding: 10px 0;
    font-size: 13px;
  }
  .chat-input-area {
    padding: 10px 12px;
  }

  .contacts-header {
    flex-direction: column;
    align-items: stretch;
  }
  .contacts-search {
    width: auto;
  }
  .btn-whatsapp {
    margin-left: 0;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .attach-btn {
    width: 36px;
    height: 36px;
  }
  .send-btn {
    width: 36px;
    height: 36px;
  }
  .input-group {
    padding: 2px 2px 2px 12px;
  }
  .chat-input {
    padding: 8px 0;
  }
  .msg-bubble {
    max-width: 85%;
  }
  .msg-avatar-small {
    width: 24px;
    height: 24px;
  }
  .msg-avatar-small i {
    font-size: 9px;
  }
  .chat-header {
    padding: 12px;
  }
  .chat-header-info h4 {
    font-size: 13px;
  }
  .chat-header-info span {
    font-size: 10px;
  }
  .contact-avatar {
    width: 36px;
    height: 36px;
  }
  .contact-name {
    font-size: 13px;
  }
  .contact-preview {
    font-size: 11px;
  }
}

/* WhatsApp button styles */
.btn-whatsapp {
  background: #25D366;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: 0.2s;
  margin-left: auto;
}
.btn-whatsapp:hover {
  background: #20b859;
  transform: scale(1.02);
}
.btn-whatsapp i {
  font-size: 14px;
}
</style>
<style>
/* ========== STYLES GLOBAUX POUR LE MODAL WHATSAPP (non scoped) ========== */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease;
}

.modal-whatsapp {
  background: #1e293b;
  border-radius: 28px;
  width: 90%;
  max-width: 560px;
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  border: 1px solid #334155;
  animation: slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: #0f172a;
  border-bottom: 1px solid #334155;
}

.modal__head-label {
  display: flex;
  align-items: center;
  gap: 14px;
}

.modal__head-icon {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #25D366, #128C7E);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: white;
  box-shadow: 0 4px 10px rgba(37, 211, 102, 0.3);
}

.modal__head-title {
  font-size: 18px;
  font-weight: 700;
  color: #f1f5f9;
}

.modal__head-sub {
  font-size: 13px;
  color: #94a3b8;
  margin-top: 2px;
}

.modal__body {
  padding: 20px 24px;
  background: #1e293b;
}

.search-wrap {
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 40px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  transition: all 0.2s;
}

.search-wrap:focus-within {
  border-color: #f59e0b;
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2);
}

.search-wrap__icon {
  color: #64748b;
  font-size: 14px;
}

.search-wrap__input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #f1f5f9;
  font-size: 14px;
}

.search-wrap__input::placeholder {
  color: #475569;
}

.whatsapp-client-list {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #334155;
  border-radius: 20px;
  background: #0f172a;
  margin-bottom: 16px;
}

.whatsapp-client-item {
  padding: 12px 16px;
  border-bottom: 1px solid #1e293b;
  transition: background 0.2s;
}

.whatsapp-client-item:last-child {
  border-bottom: none;
}

.whatsapp-client-item:hover {
  background: #1e293b;
}

.whatsapp-client-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  width: 100%;
}

.client-radio {
  width: 18px;
  height: 18px;
  accent-color: #f59e0b;
  margin: 0;
}

.client-name {
  font-weight: 600;
  color: #f1f5f9;
  font-size: 14px;
  flex: 1;
}

.client-phone,
.no-phone {
  font-size: 12px;
  color: #94a3b8;
  background: #334155;
  padding: 2px 8px;
  border-radius: 20px;
}

.no-phone {
  background: #991b1b;
  color: #fecaca;
}

.selected-info {
  font-size: 13px;
  color: #cbd5e1;
  background: #0f172a;
  padding: 8px 12px;
  border-radius: 40px;
  text-align: center;
  margin-bottom: 20px;
  border: 1px solid #334155;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #cbd5e1;
  margin-bottom: 8px;
}

.compose-box__textarea {
  width: 100%;
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 20px;
  padding: 12px 16px;
  color: #f1f5f9;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  transition: all 0.2s;
}

.compose-box__textarea:focus {
  outline: none;
  border-color: #f59e0b;
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2);
}

.template-hint {
  font-size: 11px;
  color: #64748b;
  margin-top: 6px;
}

.link-preview {
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 20px;
  padding: 14px;
  margin-top: 8px;
}

.preview-label {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  margin-bottom: 8px;
}

.link-preview code {
  font-size: 11px;
  word-break: break-all;
  color: #cbd5e1;
  background: #1e293b;
  padding: 4px 8px;
  border-radius: 12px;
  display: inline-block;
}

.modal__foot {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  background: #0f172a;
  border-top: 1px solid #334155;
}

.modal__foot--column {
  flex-direction: row;
  justify-content: flex-end;
}

.btn--ghost {
  background: transparent;
  border: 1px solid #475569;
  color: #cbd5e1;
  padding: 8px 20px;
  border-radius: 40px;
  font-weight: 500;
  transition: all 0.2s;
}

.btn--ghost:hover {
  background: #334155;
  border-color: #64748b;
  color: white;
}

.btn--whatsapp {
  background: linear-gradient(135deg, #25D366, #128C7E);
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 40px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn--whatsapp:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
}

.btn--secondary {
  background: #475569;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 40px;
  font-weight: 500;
  transition: all 0.2s;
}

.btn--secondary:hover:not(:disabled) {
  background: #334155;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments for modal */
@media (max-width: 640px) {
  .modal-whatsapp {
    width: 95%;
    max-height: 90vh;
  }
  .modal__head {
    padding: 16px;
  }
  .modal__body {
    padding: 16px;
  }
  .modal__foot {
    padding: 12px 16px;
    flex-wrap: wrap;
  }
  .modal__foot--column {
    flex-direction: column;
  }
  .modal__foot--column .btn {
    width: 100%;
    justify-content: center;
  }
  .whatsapp-client-list {
    max-height: 200px;
  }
}
</style>
